
import React from "react";
import { Button, FormHelperText, Grid } from "@mui/material";
import { formHookInputHelper } from "utils/formHookField";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import RichTextEditor from "components/Shared/RichTextEditor";

import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";

import { useParams } from "react-router-dom";

import { useAuth } from "hooks";

import { useGetDDLSystemVendorUserListQuery } from "store/services/shipmentActionApi";

const INITIAL_VALUE = {
  account: null,
  entity: null,
  shipmentId: null,
  actionType: "",
  whoCanClose: "",
  status: "Open",
  description: "",
  responsible: "",
  vendor: null,
  response: "",
  systemType: "",
};

const ShipmentActionForm = ({
  isBusy = false,
  editActionData,
  onSubmit = () => { },
  onCancel = () => { },
}) => {
  const { id: shipmentIdFromParams } = useParams();
  const { viewEntity, isVendor, loggedInUser } = useAuth();
  const { ShipmentDetail, loadingShipment } = useShipmentDetailContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      account: ShipmentDetail?.account || viewEntity?.entity?.account || null,
      entity: ShipmentDetail?.entity || viewEntity?.entity || null,
      shipmentId: ShipmentDetail
        ? { _id: ShipmentDetail._id, shipmentId: ShipmentDetail.shipmentId }
        : shipmentIdFromParams
          ? { _id: shipmentIdFromParams, shipmentId: shipmentIdFromParams }
          : null,
      vendor: ShipmentDetail?.vendor
        ? {
          id: ShipmentDetail.vendor._id,
          label: ShipmentDetail.vendor.ownerName,
        }
        : null,
    },
  });

  const systemType = watch("systemType");
  const actionTypeEnums = ["Approval", "Request", "Info"];
  const systemTypeEnums = ["Internal", "External"];
  const statusEnums = ["Open", "Read", "Closed"];

  // Dynamically set whoCanClose options based on systemType
  const whoCanCloseEnums = React.useMemo(() => {
    if (isVendor) {
      return ["Myself", "Admin"];
    }
    return systemType === "Internal" ? ["Admin"] : systemType === "External" ? ["Admin", "Vendor"] : [];
  }, [systemType, isVendor]);

  const { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers } =
    useGetDDLSystemVendorUserListQuery(
      {
        active: "true",
        type: systemType,
      },
      { skip: !systemType }
    );

  const _mappedUsers = React.useMemo(
    () => userList?.map((el) => ({ label: el?.name, id: el?._id })) || [],
    [userList]
  );

  // Auto-populate responsible and whoCanClose based on systemType
  React.useEffect(() => {
    if (systemType) {
      // Auto-populate responsible
      if (loggedInUser) {
        const responsibleUser = {
          label: loggedInUser.name,
          id: loggedInUser._id,
        };
        setValue("responsible", responsibleUser, { shouldValidate: true });
      }

      // Auto-populate whoCanClose
      if (!isVendor) {
        const defaultWhoCanClose = systemType === "Internal" ? "Admin" : "Vendor";
        setValue("whoCanClose", defaultWhoCanClose, { shouldValidate: true });
      } else {
        setValue("whoCanClose", "Myself", { shouldValidate: true });
      }
    }
  }, [systemType, loggedInUser, setValue, isVendor]);

  React.useEffect(() => {
    if (editActionData) {
      reset({
        account:
          editActionData.account ||
          ShipmentDetail?.account ||
          viewEntity?.entity?.account,
        entity:
          editActionData.entity || ShipmentDetail?.entity || viewEntity?.entity,
        shipmentId: editActionData.shipmentId
          ? typeof editActionData.shipmentId === "string"
            ? {
              _id: editActionData.shipmentId,
              shipmentId: editActionData.shipmentId,
            }
            : {
              _id: editActionData.shipmentId._id,
              shipmentId: editActionData.shipmentId.shipmentId,
            }
          : ShipmentDetail
            ? { _id: ShipmentDetail._id, shipmentId: ShipmentDetail.shipmentId }
            : { _id: shipmentIdFromParams, shipmentId: shipmentIdFromParams },
        actionType: editActionData.actionType || "",
        whoCanClose: editActionData.whoCanClose || "",
        status: editActionData.status || "",
        description: editActionData.description || "",
        response: editActionData.response || "",
        responsible: editActionData.responsible
          ? {
            label: editActionData.responsible.name,
            id: editActionData.responsible._id,
          }
          : "",
        vendor: editActionData.vendor
          ? {
            label:
              editActionData.vendor.name || editActionData.vendor.ownerName,
            id: editActionData.vendor._id,
          }
          : ShipmentDetail?.vendor
            ? {
              id: ShipmentDetail.vendor._id,
              label: ShipmentDetail.vendor.ownerName,
            }
            : null,
        systemType: editActionData.systemType || "",
      });
    } else {
      reset({
        ...INITIAL_VALUE,
        account: ShipmentDetail?.account || viewEntity?.entity?.account,
        entity: ShipmentDetail?.entity || viewEntity?.entity,
        shipmentId: ShipmentDetail
          ? { _id: ShipmentDetail._id, shipmentId: ShipmentDetail.shipmentId }
          : shipmentIdFromParams
            ? { _id: shipmentIdFromParams, shipmentId: shipmentIdFromParams }
            : null,
        vendor: ShipmentDetail?.vendor
          ? {
            id: ShipmentDetail.vendor._id,
            label: ShipmentDetail.vendor.ownerName,
          }
          : null,
      });
    }
  }, [editActionData, ShipmentDetail, shipmentIdFromParams, reset, viewEntity]);

  const handleSubmitShipmentAction = async (data) => {
    if (!data?.shipmentId?._id && !shipmentIdFromParams) {
      toast.error("Shipment ID is required");
      return;
    }
    if (!data?.account?._id) {
      toast.error("Account is required");
      return;
    }
    if (!data?.entity?._id) {
      toast.error("Entity is required");
      return;
    }
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipmentId: data?.shipmentId?._id || shipmentIdFromParams,
      responsible: data?.responsible?.id,
      vendor: data?.vendor?.id,
      id: editActionData?._id,
      response: data?.response || "",
      systemType: isVendor ? "External" : (data.systemType || ""),
    };
    onSubmit(payload);
  };


  const tinymceInit = {
    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "preview",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "paste",
      "help",
      "wordcount",
    ],
    toolbar: [
      "undo redo",
      "formatselect",
      "bold italic backcolor",
      "alignleft aligncenter alignright alignjustify",
      "bullist numlist outdent indent",
      "link",
      "removeformat",
      "help",
    ].join(" | "),
    content_style:
      "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
    license_key: "gpl",
  };

  if (loadingShipment) {
    return <LoadingWrapper loading={true} />;
  }

  return (
    <LoadingWrapper loading={isBusy}>
      <AppForm padding={3}>
        <AppForm.Content>
          <Grid container spacing={5}>
            {!isVendor && (
              <Grid item md={12}>
                <Controller
                  name="systemType"
                  control={control}
                  rules={{ required: "Please select System Type." }}
                  render={(props) => (
                    <BaseSelect
                      {...formHookInputHelper(props)}
                      fullWidth
                      required
                      label="Type"
                      options={ShipmentDetail?.customerId ?
                        ["Internal"] :
                        systemTypeEnums}
                      error={!!errors?.systemType}
                      helperText={errors?.systemType?.message}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item md={12}>
              <Controller
                name="actionType"
                control={control}
                rules={{ required: "Please select an Action Type." }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    required
                    placeholder="Action Type"
                    label="Action Type"
                    options={actionTypeEnums}
                    error={!!errors?.actionType}
                    helperText={errors?.actionType?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={12}>
              <Controller
                name="whoCanClose"
                control={control}
                rules={{ required: "Please select Who Can Close." }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    required
                    label="Who Can Close"
                    options={whoCanCloseEnums}
                    error={!!errors?.whoCanClose}
                    helperText={errors?.whoCanClose?.message}
                  />
                )}
              />
            </Grid>
            {!isVendor && (
              <Grid item md={12}>
                <Controller
                  name="responsible"
                  control={control}
                  rules={{ required: "Please select a Responsible User." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Responsible"
                      loading={loadingUsers || fetchingUsers}
                      loadingText="Loading Responsible Users..."
                      options={_mappedUsers}
                      value={field.value || null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("responsible", data, { shouldValidate: true });
                      }}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      error={!!errors?.responsible}
                      helperText={errors?.responsible?.message}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item md={12}>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Please select status." }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    label="Status"
                    disabled
                    options={statusEnums}
                    error={!!errors?.status}
                    helperText={errors?.status?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={12}>
              <FormHelperText>Description*</FormHelperText>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Please provide a Description." }}
                render={({ field: { onChange } }) => (
                  <RichTextEditor
                    initialValue={
                      editActionData?.description
                        ? editActionData.description
                        : "<p>Enter Note Content.</p>"
                    }
                    onChange={onChange}
                    height={200}
                    error={!!errors.description}
                    helperText={errors?.description?.message}
                    init={tinymceInit}
                  />
                )}
              />
            </Grid>
            {editActionData && (
              <Grid item md={12}>
                <FormHelperText>Response</FormHelperText>
                <Controller
                  name="response"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      initialValue={editActionData?.response || ""}
                      onChange={field.onChange}
                      height={200}
                      error={!!errors.response}
                      helperText={errors?.response?.message}
                      init={tinymceInit}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </AppForm.Content>
      </AppForm>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel} variant="text">
          Cancel
        </Button>
        <Button
          variant="outlined"
          disabled={isBusy}
          onClick={handleSubmit(handleSubmitShipmentAction)}
        >
          {editActionData ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </LoadingWrapper>
  );
};

export default ShipmentActionForm;